import { faArrowUpRightFromSquare, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, PopoverContent, PopoverTrigger, sourTwMerge } from '@plaidev/sour-react';
import { type Dispatch, type ReactNode, type SetStateAction, useState } from 'react';
import type { LocalizedText } from '../config/type.js';
import type { MenuGroup, MenuItem } from '../types/menuModel.js';
import { L2Icon } from './icons.js';

type Locale = 'ja' | 'en';

const tx = (text: LocalizedText | undefined, locale: Locale): string => (text ? text[locale] : '');

export type MenuGroupListProps = {
  groups: readonly MenuGroup[];
  activeMenuName?: string;
  hrefResolver?: (menu: MenuItem) => string;
  onMenuClick?: (menu: MenuItem, event: React.MouseEvent<HTMLAnchorElement>) => void;
  locale?: Locale;
  hideFirstGroupLabel?: boolean;
  expanded?: boolean;
  onMenuPopoverOpenChange?: Dispatch<SetStateAction<boolean>>;
};

export function MenuGroupList({
  groups,
  activeMenuName,
  hrefResolver,
  onMenuClick,
  locale = 'ja',
  hideFirstGroupLabel = false,
  expanded = true,
  onMenuPopoverOpenChange,
}: MenuGroupListProps): ReactNode {
  return (
    <div className="flex flex-col gap-y-[5px]">
      {groups.map((group, i) => (
        <MenuGroupSection
          key={group.name}
          group={group}
          locale={locale}
          activeMenuName={activeMenuName}
          hrefResolver={hrefResolver}
          onMenuClick={onMenuClick}
          showLabel={!(i === 0 && hideFirstGroupLabel)}
          expanded={expanded}
          onMenuPopoverOpenChange={onMenuPopoverOpenChange}
        />
      ))}
    </div>
  );
}

function MenuGroupSection({
  group,
  locale,
  activeMenuName,
  hrefResolver,
  onMenuClick,
  showLabel,
  expanded,
  onMenuPopoverOpenChange,
}: {
  group: MenuGroup;
  locale: Locale;
  activeMenuName?: string;
  hrefResolver?: MenuGroupListProps['hrefResolver'];
  onMenuClick?: MenuGroupListProps['onMenuClick'];
  showLabel: boolean;
  expanded: boolean;
  onMenuPopoverOpenChange?: Dispatch<SetStateAction<boolean>>;
}): ReactNode {
  return (
    <div>
      {showLabel && group.label ? (
        <div className={sourTwMerge('flex h-5 items-center my-[5px]', expanded ? 'px-2' : 'mx-2 justify-center')}>
          {expanded ? (
            <p className="text-xs font-semibold uppercase tracking-wide text-muted whitespace-nowrap">{tx(group.label, locale)}</p>
          ) : (
            <span className="h-px w-4 bg-primitive-gray-alpha-300" />
          )}
        </div>
      ) : null}
      <ul className="flex flex-col gap-y-0.5">
        {group.menus.map((menu) =>
          menu.popoverMenus && menu.popoverMenus.length > 0 ? (
            <li key={menu.name}>
              <MenuPopoverItem
                menu={menu}
                locale={locale}
                activeMenuName={activeMenuName}
                hrefResolver={hrefResolver}
                onMenuClick={onMenuClick}
                onMenuPopoverOpenChange={onMenuPopoverOpenChange}
              />
            </li>
          ) : (
            <li key={menu.name}>
              <MenuItemLink
                menu={menu}
                locale={locale}
                isActive={activeMenuName === menu.name}
                hrefResolver={hrefResolver}
                onMenuClick={onMenuClick}
                expanded={expanded}
              />
            </li>
          ),
        )}
      </ul>
    </div>
  );
}

function MenuItemLink({
  menu,
  locale,
  isActive,
  hrefResolver,
  onMenuClick,
  expanded,
}: {
  menu: MenuItem;
  locale: Locale;
  isActive: boolean;
  hrefResolver?: MenuGroupListProps['hrefResolver'];
  onMenuClick?: MenuGroupListProps['onMenuClick'];
  expanded: boolean;
}): ReactNode {
  const href = hrefResolver ? hrefResolver(menu) : (menu.path?.path ?? '#');
  const label = tx(menu.label, locale) || menu.name;
  const isAdminUi = menu.options?.adminUi === true;

  return (
    <a
      href={href}
      onClick={(e) => onMenuClick?.(menu, e)}
      className={[
        'flex h-9 cursor-pointer items-center gap-x-2.5 rounded-md px-2 text-sm font-medium transition-colors',
        isActive ? 'bg-primary-light text-primary font-semibold' : 'text-default hover:bg-action-subtle-hover',
        isAdminUi ? 'admin-ui' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      target={menu.options?.openAnotherTab ? '_blank' : undefined}
      rel={menu.options?.openAnotherTab ? 'noopener noreferrer' : undefined}
    >
      {menu.icon ? (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center text-inherit">
          <L2Icon icon={menu.icon} />
        </span>
      ) : null}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {expanded && menu.options?.openAnotherTab ? (
        <FontAwesomeIcon
          icon={faArrowUpRightFromSquare}
          className="ml-auto shrink-0 text-[10px] text-muted"
          aria-label="新しいタブで開く"
        />
      ) : null}
      {expanded && menu.options?.isBeta ? (
        <span className="ml-auto rounded-sm bg-action-subtle px-1 text-[10px] font-semibold text-muted">BETA</span>
      ) : null}
      {expanded && menu.options?.isNew ? (
        <span className="ml-auto rounded-sm bg-primary-light px-1 text-[10px] font-semibold text-primary">NEW</span>
      ) : null}
    </a>
  );
}

function MenuPopoverItem({
  menu,
  locale,
  activeMenuName,
  hrefResolver,
  onMenuClick,
  onMenuPopoverOpenChange,
}: {
  menu: MenuItem;
  locale: Locale;
  activeMenuName?: string;
  hrefResolver?: MenuGroupListProps['hrefResolver'];
  onMenuClick?: MenuGroupListProps['onMenuClick'];
  onMenuPopoverOpenChange?: Dispatch<SetStateAction<boolean>>;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const groups = menu.popoverMenus ?? [];
  const label = tx(menu.label, locale) || menu.name;
  const isActive = groups.some((g) => g.items.some((item) => item.name === activeMenuName));
  const isAdminUi = menu.options?.adminUi === true;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onMenuPopoverOpenChange?.(next);
  };

  const trigger = (
    <button
      type="button"
      className={[
        'flex h-9 w-full cursor-pointer items-center gap-x-2.5 rounded-md px-2 text-sm font-medium transition-colors',
        isActive || open ? 'bg-primary-light text-primary font-semibold' : 'text-default hover:bg-action-subtle-hover',
        isAdminUi ? 'admin-ui' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center text-inherit">
        <L2Icon icon={menu.icon} />
      </span>
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      <FontAwesomeIcon icon={faChevronRight} className="shrink-0 text-xs text-muted" />
    </button>
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={4}
        className="z-50 max-h-[640px] w-[256px] overflow-y-auto rounded-xl border border-muted p-2"
      >
        <div className="flex flex-col gap-y-3">
          {groups.map((g) => (
            <div key={g.name}>
              {g.label ? <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted">{tx(g.label, locale)}</p> : null}
              <ul className="flex flex-col gap-y-0.5">
                {g.items.map((item) => (
                  <li key={item.name}>
                    <MenuItemLink
                      menu={item}
                      locale={locale}
                      isActive={activeMenuName === item.name}
                      hrefResolver={hrefResolver}
                      onMenuClick={(clickedMenu, event) => {
                        onMenuClick?.(clickedMenu, event);
                        handleOpenChange(false);
                      }}
                      expanded
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
